import api, { setToken, clearToken } from "./api";

interface LoginResponse {
  access_token: string;
  token_type: string;
}

interface UserInfo {
  id: number;
  username: string;
  email: string;
  full_name: string | null;
  employee_id?: string | null;
  department: string | null;
  job_title: string | null;
  work_stage_id?: number | null;
  work_stage_name?: string | null;
  is_active: boolean;
  is_superuser: boolean;
  role_id: number | null;
  role_name: string | null;
  permissions?: string[];
}

interface RegisterOptions {
  departments: { id: number; name: string }[];
  job_types: { id: number; name: string }[];
  work_stages: { id: number; name: string; job_type_id?: number | null }[];
}

interface AdminNotification {
  id: number;
  category: string;
  title: string;
  content: string | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
}

type LoginPublicKeyResponse = {
  algorithm: string;
  public_key: string;
};

const REMEMBERED_USERNAME_KEY = "remembered_username";
const REMEMBERED_PASSWORD_KEY = "remembered_password_cipher";
const REMEMBERED_PASSWORD_IV_KEY = "remembered_password_iv";
const REMEMBERED_PASSWORD_SECRET_KEY = "remembered_password_secret";
const REMEMBERED_PASSWORD_MODE_KEY = "remembered_password_mode";
let cachedLoginPublicKeyPromise: Promise<CryptoKey | null> | null = null;
let cachedLoginPublicKeyResponsePromise: Promise<LoginPublicKeyResponse | null> | null = null;

async function getLoginPublicKeyResponse(): Promise<LoginPublicKeyResponse | null> {
  if (!cachedLoginPublicKeyResponsePromise) {
    cachedLoginPublicKeyResponsePromise = api
      .get<LoginPublicKeyResponse>("/auth/public-key")
      .catch(() => null);
  }
  return cachedLoginPublicKeyResponsePromise;
}

function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem
    .replace("-----BEGIN PUBLIC KEY-----", "")
    .replace("-----END PUBLIC KEY-----", "")
    .replace(/\s+/g, "");
  return base64ToBytes(base64).buffer;
}

async function getLoginCryptoKey(): Promise<CryptoKey | null> {
  if (!window.crypto?.subtle) return null;
  if (!cachedLoginPublicKeyPromise) {
    cachedLoginPublicKeyPromise = getLoginPublicKeyResponse()
      .then(async (data) => {
        if (!data) return null;
        return window.crypto.subtle.importKey(
          "spki",
          pemToArrayBuffer(data.public_key),
          {
            name: "RSA-OAEP",
            hash: "SHA-256",
          },
          false,
          ["encrypt"],
        );
      })
      .catch(() => null);
  }
  return cachedLoginPublicKeyPromise;
}

function deriveLegacyLoginSecret(seed: string): Uint8Array {
  const source = new TextEncoder().encode(seed || "medical-rag");
  const secret = new Uint8Array(32);
  source.forEach((byte, index) => {
    const pos = index % secret.length;
    secret[pos] = (secret[pos] + byte + index) % 256;
    const alt = (index * 7) % secret.length;
    secret[alt] = secret[alt] ^ ((byte + 17) % 256);
  });
  return secret;
}

async function encryptLoginPassword(password: string): Promise<string | null> {
  const key = await getLoginCryptoKey();
  const encoded = new TextEncoder().encode(password);
  if (key && window.crypto?.subtle) {
    const encrypted = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      key,
      encoded,
    );
    return bytesToBase64(new Uint8Array(encrypted));
  }

  const keyResponse = await getLoginPublicKeyResponse();
  if (!keyResponse?.public_key) return null;
  const secret = deriveLegacyLoginSecret(keyResponse.public_key);
  return `legacy-password-v1:${bytesToBase64(xorCipher(encoded, secret))}`;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function getRememberedPasswordCryptoKey(): Promise<CryptoKey | null> {
  if (!window.crypto?.subtle) return null;

  let rawKeyBase64 = localStorage.getItem(REMEMBERED_PASSWORD_SECRET_KEY);
  if (!rawKeyBase64) {
    const rawKey = window.crypto.getRandomValues(new Uint8Array(32));
    rawKeyBase64 = bytesToBase64(rawKey);
    localStorage.setItem(REMEMBERED_PASSWORD_SECRET_KEY, rawKeyBase64);
  }

  return window.crypto.subtle.importKey(
    "raw",
    base64ToBytes(rawKeyBase64),
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

function getRememberedPasswordFallbackSecret(): Uint8Array {
  let rawKeyBase64 = localStorage.getItem(REMEMBERED_PASSWORD_SECRET_KEY);
  if (!rawKeyBase64) {
    const rawKey = window.crypto?.getRandomValues
      ? window.crypto.getRandomValues(new Uint8Array(32))
      : Uint8Array.from(Array.from({ length: 32 }, (_, index) => (index * 17 + 29) % 255));
    rawKeyBase64 = bytesToBase64(rawKey);
    localStorage.setItem(REMEMBERED_PASSWORD_SECRET_KEY, rawKeyBase64);
  }
  return base64ToBytes(rawKeyBase64);
}

function xorCipher(input: Uint8Array, secret: Uint8Array): Uint8Array {
  const output = new Uint8Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    output[i] = input[i] ^ secret[i % secret.length];
  }
  return output;
}

async function saveRememberedCredentials(username: string, password: string): Promise<void> {
  localStorage.setItem(REMEMBERED_USERNAME_KEY, username);

  const key = await getRememberedPasswordCryptoKey();
  if (!key) {
    const encoded = new TextEncoder().encode(password);
    const fallbackSecret = getRememberedPasswordFallbackSecret();
    localStorage.setItem(REMEMBERED_PASSWORD_MODE_KEY, "xor");
    localStorage.setItem(REMEMBERED_PASSWORD_IV_KEY, "");
    localStorage.setItem(REMEMBERED_PASSWORD_KEY, bytesToBase64(xorCipher(encoded, fallbackSecret)));
    return;
  }

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(password);
  const encrypted = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  localStorage.setItem(REMEMBERED_PASSWORD_MODE_KEY, "aes");
  localStorage.setItem(REMEMBERED_PASSWORD_IV_KEY, bytesToBase64(iv));
  localStorage.setItem(REMEMBERED_PASSWORD_KEY, bytesToBase64(new Uint8Array(encrypted)));
}

async function loadRememberedCredentials(): Promise<{ username: string; password: string }> {
  const username = localStorage.getItem(REMEMBERED_USERNAME_KEY) || "";
  const cipherText = localStorage.getItem(REMEMBERED_PASSWORD_KEY) || "";
  const ivText = localStorage.getItem(REMEMBERED_PASSWORD_IV_KEY) || "";
  const mode = localStorage.getItem(REMEMBERED_PASSWORD_MODE_KEY) || "aes";
  if (!cipherText || (mode !== "xor" && !ivText)) return { username, password: "" };

  try {
    if (mode === "xor") {
      const fallbackSecret = getRememberedPasswordFallbackSecret();
      const decrypted = xorCipher(base64ToBytes(cipherText), fallbackSecret);
      return {
        username,
        password: new TextDecoder().decode(decrypted),
      };
    }
    const key = await getRememberedPasswordCryptoKey();
    if (!key) return { username, password: "" };

    const decrypted = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: base64ToBytes(ivText) },
      key,
      base64ToBytes(cipherText),
    );
    return {
      username,
      password: new TextDecoder().decode(decrypted),
    };
  } catch {
    localStorage.removeItem(REMEMBERED_PASSWORD_KEY);
    localStorage.removeItem(REMEMBERED_PASSWORD_IV_KEY);
    localStorage.removeItem(REMEMBERED_PASSWORD_MODE_KEY);
    return { username, password: "" };
  }
}

function clearRememberedCredentials() {
  localStorage.removeItem(REMEMBERED_USERNAME_KEY);
  localStorage.removeItem(REMEMBERED_PASSWORD_KEY);
  localStorage.removeItem(REMEMBERED_PASSWORD_IV_KEY);
  localStorage.removeItem(REMEMBERED_PASSWORD_MODE_KEY);
}

async function login(username: string, password: string, remember = true): Promise<LoginResponse> {
  const passwordCipher = await encryptLoginPassword(password);
  if (!passwordCipher) {
    throw new Error("登录加密初始化失败，请刷新页面后重试");
  }
  const data = await api.post<LoginResponse>("/auth/login-json", {
    username,
    password_cipher: passwordCipher,
  });
  setToken(data.access_token, remember);
  return data;
}

async function register(data: {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  employee_id?: string;
  department?: string;
  job_title?: string;
  department_id?: number;
  job_type_id?: number;
  work_stage_id?: number;
}): Promise<UserInfo> {
  return api.post<UserInfo>("/auth/register", data);
}

async function sendResetCode(username: string, email: string): Promise<{ message: string }> {
  return api.post<{ message: string }>("/auth/send-reset-code", { username, email });
}

async function resetPasswordWithCode(payload: {
  username: string;
  email: string;
  code: string;
  new_password: string;
}): Promise<{ message: string }> {
  return api.post<{ message: string }>("/auth/reset-password-with-code", payload);
}

async function getRegisterOptions(): Promise<RegisterOptions> {
  return api.get<RegisterOptions>("/auth/register-options");
}

function logout() {
  clearToken();
  window.location.href = "/login";
}

async function getCurrentUser(): Promise<UserInfo> {
  // The backend doesn't have a /me endpoint yet, but we can decode from token
  // For now, we'll use the admin users endpoint if superuser
  return api.get<UserInfo>("/auth/me");
}

async function changePassword(oldPassword: string, newPassword: string): Promise<{ message: string }> {
  return api.post<{ message: string }>("/auth/change-password", {
    old_password: oldPassword,
    new_password: newPassword,
  });
}

async function getNotifications(): Promise<AdminNotification[]> {
  return api.get<AdminNotification[]>("/auth/notifications");
}

async function markNotificationRead(id: number): Promise<{ message: string }> {
  return api.put<{ message: string }>(`/auth/notifications/${id}/read`);
}

export type { LoginResponse, UserInfo, RegisterOptions, AdminNotification };
export {
  login,
  register,
  logout,
  getCurrentUser,
  changePassword,
  getRegisterOptions,
  getNotifications,
  markNotificationRead,
  sendResetCode,
  resetPasswordWithCode,
  saveRememberedCredentials,
  loadRememberedCredentials,
  clearRememberedCredentials,
};
